import {
  BPCDisplayConfigField,
  BPCDisplayConfigResponse,
} from '@core/interfaces/bpc-display-config';

import BPCGenericApiResponse from '@core/interfaces/bpc-generic-api-response';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Subscription } from 'rxjs';
import PaginationInterface from '@core/interfaces/pagination-interface';
import BPCListQueryConfig from '@core/interfaces/bpc-list-query-config';
import SearchAndOrder from '@core/interfaces/bpc-search-and-order';
import { isNull } from 'lodash';

@Component({
  name: 'bpc-entity-table-provider',
  components: {},
})
export default class EntityTableProvider extends Vue {
  @Prop(String) readonly entity: string;
  @Prop(String) readonly fetchPath: string;
  @Prop(String) readonly orderPath: string;
  @Prop(String) readonly configPath: string;
  @Prop(String) readonly configFileName: string;
  @Prop({ type: String, default: 'table' }) readonly displayConfigName: string;
  @Prop({ type: Array, default: null }) readonly selectConfigName: string[];
  @Prop({ type: String, default: 'export' }) readonly exportConfigName: string;
  @Prop({ type: Number, default: 300 }) readonly searchTimeout: number;
  @Prop(Array) readonly initialFilters: Record<string, any>[];
  @Prop(String) readonly mode: string;
  @Prop({ type: Boolean, default: false }) readonly withFilter: boolean; // If is false, the provider do the request to get entities in created method, if not the request is done in loadQueryParamsFromURL
  @Prop({ type: Boolean, default: false }) readonly withTabFilter: boolean;
  @Prop({ type: Boolean, default: false }) readonly withCategoryFilter: boolean;
  @Prop({ type: Boolean, default: false }) readonly blockLoadEntities: boolean;
  @Prop({
    type: Array,
    default: () => ['configLoaded', 'paginationLoaded', 'orderLoaded', 'urlParametersLoaded'],
  })
  readonly flagsToCheckBeforeLoadEntities: Array<any>;
  @Prop({ type: String, default: 'table' }) readonly storeName: string;
  @Prop() readonly selectedItem: any;
  @Prop({ type: Boolean, default: false }) readonly ignoreURLParams: boolean;
  @Prop({ type: Boolean, default: false }) readonly select: boolean;
  @Prop({ type: Boolean, default: false }) readonly withoutActions: boolean;
  @Prop(Number) readonly checkColumOrder: number;
  @Prop(Number) readonly forcedPaginationPerPage: number;

  @Prop({ type: Boolean, default: false }) readonly onlyLoadOnOpen: boolean;
  @Prop({ type: Boolean, default: false }) readonly strictSearch: boolean;

  showTableHeader: boolean = false;
  showTableTitlesRow: boolean = true;
  counterGetEntity = 0;
  firstLoadEntities: boolean = false;
  loading = true;
  downloadingExport = false;
  loadingConfig = true;
  loadingURLQueryParams = true; // Until this param is false, the filters of the table has not effect
  loadingTabURLQueryParams = true; // Until this param is false, the filters of the table has not effect

  accumulateItems = false;

  entitySubscription: Subscription;
  exportSubscription: Subscription;
  orderSubscription: Subscription;
  entityConfigSubscription: Subscription;

  items: object[] = [];
  fields: BPCDisplayConfigField[] = [];
  tableSortable: boolean = false;
  selectable: boolean = true;

  initialView = 'table';
  availableViews = ['table'];
  cardType = null;
  gridCols = [];

  noCached = false;

  pagination: PaginationInterface = {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    from: 0,
    to: 0,
    total: 0,
  };

  // search: string | null = 'alojomora';
  // orderBy: string | null = null;
  // orderDirection: string | null = 'desc';

  searchAndOrder: SearchAndOrder = {
    search: '',
    orderBy: 'id',
    orderDirection: 'desc',
  };

  queryConfig: BPCListQueryConfig = { params: {} };

  get userLang(): string {
    return this.$store.getters['auth/userLang'] || this.$i18n.locale;
  }

  get canLoadEntities(): boolean {
    return (
      this.$store.getters[`${this.storeName}/canLoadEntities`]({
        entity: this.entity,
        flags: this.flagsToCheckBeforeLoadEntities,
      }) && !this.blockLoadEntities
    );
    // return true;
  }

  public get resolvedConfigFileName(): string {
    return this.configFileName ? this.configFileName : this.entity;
  }

  get canLoadQueryParams(): boolean {
    return this.$store.getters[`${this.storeName}/canLoadQueryParams`]({
      entity: this.entity,
      filters: {
        filtersLoaded: this.withFilter,
        tabFiltersLoaded: this.withTabFilter,
        categoryFiltersLoaded: this.withCategoryFilter,
      },
    });
  }

  get entityPath(): string {
    return this.fetchPath ? this.fetchPath : this.$bpcApiRouteHelper.getEntityPath(this.entity);
  }

  get entityDisplayConfigSlug(): string {
    return this.configPath
      ? this.configPath
      : `${this.resolvedConfigFileName}.displays.${this.displayConfigName}`;
  }

  get orderEntityPath(): string {
    return this.orderPath
      ? this.orderPath
      : this.$bpcApiRouteHelper.getOrderEntityPath(this.entity);
  }

  get currentPage(): number | null {
    return this.pagination ? Number(this.pagination.current_page) : null;
  }

  get perPage(): number | null {
    return this.forcedPaginationPerPage
      ? this.forcedPaginationPerPage
      : this.pagination
      ? Number(this.pagination.per_page)
      : null;
  }

  get search(): string | null {
    return this.searchAndOrder.search || '';
  }
  get orderBy(): string | null {
    return this.searchAndOrder.orderBy || '';
  }
  get orderDirection(): string | null {
    return this.searchAndOrder.orderDirection || '';
  }

  get checkedAll(): { value: boolean } {
    let unchecked = null;

    if (this.items) {
      unchecked = this.items.find((item: { checked?: boolean }) => !item.checked);
    }

    return { value: !unchecked };
  }

  mounted() {
    this.$emit('component-mounted');
  }
  created() {
    this.$store.commit(`${this.storeName}/clearCanLoadEntities`, this.entity);
    if (!this.onlyLoadOnOpen || this.selectedItem) {
      this.getConfig();
    }
  }

  getConfig() {
    this.$store.commit(`${this.storeName}/startLoadingConfig`, this.entity);
    this.$store.commit(`${this.storeName}/startLoadingPagination`, this.entity);
    this.$store.commit(`${this.storeName}/startLoadingOrder`, this.entity);
    this.loadingConfig = true;

    if (this.entityConfigSubscription) {
      this.entityConfigSubscription.unsubscribe();
    }
    this.entityConfigSubscription = this.$bpcConfig
      .getEntityConfig(this.entityDisplayConfigSlug)
      .subscribe(
        (res: BPCDisplayConfigResponse) => {
          this.fields = res.data?.fields;
          this.tableSortable = res.data?.tableSortable;
          if (!this.forcedPaginationPerPage) {
            if (this.getInRoute('per_page')) {
              // Has more priority the value in url than the config
              this.$set(this.pagination, 'per_page', this.getInRoute('per_page'));
            } else {
              if (res.data.itemsPerPage) {
                this.$set(this.pagination, 'per_page', res.data.itemsPerPage);
              }
            }
          }

          if (this.getInRoute('orderby')) {
            // Has more priority the value in url than the config
            this.$set(this.searchAndOrder, 'orderBy', this.getInRoute('orderby'));
          } else {
            if (res.data.orderBy) {
              this.$set(this.searchAndOrder, 'orderBy', res.data.orderBy);
            }
          }
          if (this.getInRoute('orderDirection')) {
            // Has more priority the value in url than the config
            this.$set(this.searchAndOrder, 'orderDirection', this.getInRoute('orderDirection'));
          } else {
            if (res.data.orderDirection) {
              this.$set(this.searchAndOrder, 'orderDirection', res.data.orderDirection);
            }
          }

          if (res.data.initialView) {
            this.initialView = res.data.initialView;
          }

          if (res.data.availableViews) {
            this.availableViews = res.data.availableViews;
          }

          this.showTableHeader = res.data.showTableHeader;
          this.showTableTitlesRow = res.data.showTableTitlesRow;
          this.selectable = res.data.selectable;
          this.cardType = res.data.cardType;
          this.gridCols = res.data.gridCols;
          this.noCached = res.data.noCached;

          if (this.selectable && !this.fields.some(value => value.key === 'checked')) {
            this.addCheckColumns();
          }

          if (!this.fields.some(value => value.key === 'actions')) {
            this.addActionColumns();
          }

          this.orderColumns();
          this.$emit('on-load-config', res);
        },
        error => {
          console.log(error);
        },
        () => {
          this.$store.commit(`${this.storeName}/stopLoadingConfig`, this.entity);
          this.$store.commit(`${this.storeName}/stopLoadingPagination`, this.entity);
          this.$store.commit(`${this.storeName}/stopLoadingOrder`, this.entity);
          this.loadingConfig = false;
        },
      );
  }

  orderColumns() {
    this.fields.sort((a, b) => a.order - b.order);
  }

  addCheckColumns(): void {
    this.fields.unshift({
      order: this.checkColumOrder ? this.checkColumOrder : -1,
      key: 'checked',
      label: '',
      class: '',
      help: '',
      thStyle: { width: '20px' },
      tdStyle: { width: '20px' },
      sortable: false,
      displayType: 'checked',
    });
  }

  addActionColumns(): void {
    if (!this.withoutActions) {
      this.fields.push({
        order: 10000,
        key: 'actions',
        label: 'Actions',
        class: '',
        help: '',
        thStyle: { width: '50px' },
        tdStyle: { width: '50px' },
        sortable: false,
        displayType: 'actions',
      });
    }
  }

  onCheckAll(val: boolean) {
    this.items.forEach((item: object) => {
      this.$set(item, 'checked', val);
    });
    // console.log('onCheckAll');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCheckRow(row: any) {
    console.log(row);
  }

  setQueryConfig() {
    this.queryConfig.params.page = this.currentPage;
    this.queryConfig.params.per_page = this.perPage;

    if (this.search) {
      this.$set(this.queryConfig.params, 'search', this.search);
    } else {
      this.$delete(this.queryConfig.params, 'search');
    }

    if (this.orderBy) {
      this.$set(this.queryConfig.params, 'orderBy', this.orderBy);
      this.$set(this.queryConfig.params, 'orderDirection', this.orderDirection);
    } else {
      this.$delete(this.queryConfig.params, 'orderBy');
      this.$delete(this.queryConfig.params, 'orderDirection');
    }
    if (this.initialFilters) {
      this.initialFilters.forEach(filter => {
        let filterValue = filter['value'];
        if (filter['storeValue']) {
          filterValue = this.$store.getters[filter['storeValue']];
        }
        this.$set(this.queryConfig.params, filter['filterField'], filterValue);
      });
    }

    const filterModel = this.$store.getters[`${this.storeName}/filterModel`](this.entity);
    for (const key in filterModel) {
      if (this.queryConfig.params[key] != filterModel[key]) {
        this.$set(this.queryConfig.params, key, filterModel[key]);
      }
    }

    if (this.noCached) {
      this.$set(this.queryConfig, 'headers', {
        'BPC-CACHE': 'NO-CACHED',
      });
    }
  }

  public getEntities(selectConfigNameIndex = 0): void {
    if (this.canLoadEntities) {
      this.$emit('start-loading-entities');
      this.$store.commit(`${this.storeName}/startLoadingEntities`, this.entity);
      this.loading = !selectConfigNameIndex;
      this.setQueryConfig();

      if (this.entitySubscription) {
        this.entitySubscription.unsubscribe();
      }
      let request = null;
      const queryConfig = JSON.parse(JSON.stringify(this.queryConfig)); // Object.assign doesn't work well. It keep the reference of the object and we want a copy
      this.addSelectedItemToQueryConfig(queryConfig);
      this.addSelectToQueryConfig(selectConfigNameIndex, queryConfig);
      if (this.strictSearch) {
        queryConfig.params['strictSearch'] = this.strictSearch;
      }
      request = this.getBPCRequestService().get(this.entityPath, queryConfig);
      this.entitySubscription = request.subscribe(
        (res: BPCGenericApiResponse) => {
          if (this.accumulateItems) {
            // NOTE if it duplicate the items, try to add a filter
            res.data.forEach(element => {
              let exists = false;
              this.items.forEach(item => {
                if (item['id'] === element['id']) {
                  exists = true;
                }
              });
              if (!exists) {
                this.items.push(element);
              }
            });
          } else {
            if (!selectConfigNameIndex) {
              // If there is the first part of the request then get the data and set the item
              this.items = res.data;
              this.counterGetEntity++;
            } else {
              // IF not, we merge the new data with items of the table
              this.items = this.mergeResults(this.items, res.data);
            }
            if (this.selectConfigName && this.selectConfigName[selectConfigNameIndex + 1]) {
              // We check if there are more part for the request
              this.getEntities(selectConfigNameIndex + 1);
            }
          }
          if (res.meta) {
            this.pagination = res.meta;
            // this.$set(this.pagination, 'current_page', res.meta.current_page);
          }
          this.$emit('on-load-items');
          this.$emit('end-loading-entities');
        },
        error => {
          console.log(error);
        },
        () => {
          this.$store.commit(`${this.storeName}/stopLoadingEntities`, this.entity);
          this.loading = false;
          this.firstLoadEntities = true;
          this.accumulateItems = false;
        },
      );
    }
  }
  public orderEntities(data: { dragged_id: number; replaced_id: number }): void {
    this.setQueryConfig();

    if (this.orderSubscription) {
      this.orderSubscription.unsubscribe();
    }
    let request = null;
    request = this.$bpcApi.post(this.orderEntityPath, data);
    this.orderSubscription = request.subscribe(
      (res: BPCGenericApiResponse) => {
        this.getEntities();
      },
      error => {
        console.log(error);
      },
    );
  }
  public clearEntities(): void {
    this.items = [];
    this.pagination = {
      current_page: 1,
      last_page: 1,
      per_page: 15,
      from: 0,
      to: 0,
      total: 0,
    };
  }

  public async exportCancel() {
    if (this.exportSubscription) {
      return this.exportSubscription.unsubscribe();
    }
  }

  public async export() {
    this.downloadingExport = true;
    return new Promise((resolve, reject) => {
      if (this.canLoadEntities) {
        this.setQueryConfig();

        const queryConfig = Object.assign(this.queryConfig);
        // if (this.selectConfigName) {
        //   const config = this.$store.getters['config/entityConfig'](this.entity.replace('-', '_'));
        //   this.selectConfigName.map((selectConfigName) => {
        //     if (config && config.selects && config.selects[this.selectConfigName]) {
        //       queryConfig.params['columns'] = config.selects[this.selectConfigName].fields;
        //     }
        //   })
        // }
        if (this.exportSubscription) {
          this.exportSubscription.unsubscribe();
        }
        const selected = this.items.filter(item => !!item['checked']).map(item => item['id']);
        queryConfig.params['selectedItems'] = selected.join(',');
        queryConfig.params['exportConfigName'] = this.exportConfigName;
        queryConfig.params['userLang'] = this.userLang;
        queryConfig.params['os'] = this.getUserOS();

        const request = this.$bpcApi.download(
          `${this.entityPath}/export`,
          `${this.entity}-export.csv`,
          queryConfig,
        );
        this.exportSubscription = request.subscribe(
          (res: BPCGenericApiResponse) => {
            // Download the response file
            this.$emit('on-export-items');
            this.downloadingExport = false;
            resolve(res);
          },
          error => {
            console.log(error);
            reject(error);
          },
        );
      } else {
        resolve(true);
      }
    });
  }

  public async exportAll() {
    this.downloadingExport = true;
    return new Promise((resolve, reject) => {
      if (this.canLoadEntities) {
        this.setQueryConfig();

        if (this.exportSubscription) {
          this.exportSubscription.unsubscribe();
        }
        let request = null;
        const queryConfig = Object.assign(this.queryConfig);
        // if (this.selectConfigName) {
        //   const config = this.$store.getters['config/entityConfig'](this.entity.replace('-', '_'));
        //   if (config && config.selects && config.selects[this.selectConfigName]) {
        //     queryConfig.params['columns'] = config.selects[this.selectConfigName].fields;
        //   }
        // }

        if (this.displayConfigName) {
          queryConfig.params['exportConfigName'] = this.exportConfigName;
        }

        queryConfig.params['userLang'] = this.userLang;
        queryConfig.params['os'] = this.getUserOS();

        this.$delete(queryConfig.params, 'selectedItems');
        request = this.$bpcApi.download(
          `${this.entityPath}/export-all`,
          `all-${this.entity}-export.csv`,
          queryConfig,
        );
        this.exportSubscription = request.subscribe(
          (res: BPCGenericApiResponse) => {
            // Download the response file
            this.$emit('on-export-all-items');
            this.downloadingExport = false;
            resolve(res);
          },
          error => {
            console.log(error);
            reject(error);
          },
        );
      } else {
        resolve(true);
      }
    });
  }

  getUserOS(): string {
    const userAgent = window.navigator.userAgent;
    const platform = window.navigator.platform;
    const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
    const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
    const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
    let os = null;

    if (macosPlatforms.indexOf(platform) !== -1) {
      os = 'Mac OS';
    } else if (iosPlatforms.indexOf(platform) !== -1) {
      os = 'iOS';
    } else if (windowsPlatforms.indexOf(platform) !== -1) {
      os = 'Windows';
    } else if (/Android/.test(userAgent)) {
      os = 'Android';
    } else if (!os && /Linux/.test(platform)) {
      os = 'Linux';
    }

    return os;
  }

  searchIfStopWriting() {
    const text = this.search;
    setTimeout(() => {
      if (text === this.search) {
        // this.getEntities();
        if (this.pagination.current_page != 1) {
          this.pagination.current_page = this.getDefaultPage(); // This must to force a getEntities
        } else {
          this.getEntities();
        }
        this.updateURLWithQueryParams();
      }
    }, this.searchTimeout);
  }

  @Watch('searchAndOrder', { deep: true })
  onChangeSearchAndOrder() {
    this.$emit('on-change-search-and-order', this.searchAndOrder);
  }

  @Watch('perPage')
  onPerPageChange() {
    if (this.pagination.current_page === 1 && this.firstLoadEntities) {
      this.getEntities();
      this.updateURLWithQueryParams();
    } else {
      this.pagination.current_page = this.getDefaultPage();
    }
  }

  @Watch('currentPage')
  onCurrentPageChange() {
    if (this.firstLoadEntities) {
      this.getEntities();
      this.updateURLWithQueryParams();
    }
  }

  @Watch('search')
  onSearchChange(val: string) {
    if (!val && this.firstLoadEntities) {
      if (this.pagination.current_page === 1 && this.firstLoadEntities) {
        this.getEntities();
        this.updateURLWithQueryParams();
      } else {
        this.pagination.current_page = this.getDefaultPage();
      }
    } else {
      this.searchIfStopWriting();
    }
  }

  @Watch('orderBy')
  onOrderByChange() {
    if (this.pagination.current_page === 1 && this.firstLoadEntities) {
      this.getEntities();
      this.updateURLWithQueryParams();
    } else {
      this.pagination.current_page = this.getDefaultPage();
    }
  }

  @Watch('orderDirection')
  onOrderDirectionChange() {
    if (this.pagination.current_page === 1 && this.firstLoadEntities) {
      this.getEntities();
      this.updateURLWithQueryParams();
    } else {
      this.pagination.current_page = this.getDefaultPage();
    }
  }

  @Watch('canLoadEntities')
  onCanLoadEntities() {
    if (this.canLoadEntities === true && !this.firstLoadEntities) {
      this.getEntities();
    }
  }

  @Watch('canLoadQueryParams')
  onCanLoadQueryParams() {
    if (this.canLoadQueryParams === true) {
      this.loadQueryParamsFromURL();
    }
  }

  onChangeOrder(event: { dragged_id: number; replaced_id: number }) {
    if (this.tableSortable) {
      this.orderEntities(event);
    }
  }

  // @Watch('syncedCheckedAll')
  // onSyncedCheckedAllChange(val: boolean) {
  //   this.onCheckAll(val);
  // }

  private getBPCRequestService() {
    if (this.mode === 'storage') {
      return this.$bpcStorage;
    } else {
      return this.$bpcApi;
    }
  }
  // It's called when the std-filter component has loaded the config and it's in charge of get the query params of the routes and load in the queryConfig prop
  // Also do the first request for get the entities (items of the table) DEPRECATED: Now set in Vuex that params are loaded before get first time entities
  private loadQueryParamsFromURL(): void {
    this.$store.commit(`${this.storeName}/startLoadingUrlParameters`, this.entity);
    if (!this.ignoreURLParams) {
      const urlQueryParams = JSON.parse(JSON.stringify(this.$route.query), (k, v) => {
        if (!isNaN(v) && !isNull(v)) {
          return parseInt(v);
        }
        return v;
      });
      const urlRouteParams = JSON.parse(JSON.stringify(this.$route.params), (k, v) => {
        if (!isNaN(v) && !isNull(v)) {
          return parseInt(v);
        }
        return v;
      });
      // const urlRouteParams = Object.fromEntries(
      //   Object.entries(params).filter(([key, value]) => key.endsWith('_id') === true),
      // );

      this.queryConfig.params = {
        ...this.queryConfig.params,
        ...urlQueryParams,
        ...urlRouteParams,
      };
    }
    if (this.getInRoute('search')) {
      this.$set(this.searchAndOrder, 'search', this.getInRoute('search'));
    }
    this.$emit('on-load-query-params-from-url', {
      params: this.queryConfig.params,
    });
  }

  private getDefaultPage() {
    let result = 1;
    if (this.getInRoute('page') && !this.firstLoadEntities) {
      // Before of the first load we want that the current_page was loaded of the url (if exists)
      result = this.getInRoute('page');
    }
    return result;
  }

  private updateURLWithQueryParams() {
    if (!this.ignoreURLParams) {
      this.$router.push({ query: this.queryConfig.params }).catch(e => {
        return null;
      });
    }
  }
  private getInRoute(param: string): any {
    if (!this.ignoreURLParams) {
      return this.$route.query[param];
    }
    return null;
  }
  private mergeResults(currentItems, itemsToMerge) {
    const resultItems = currentItems.map(currentItem => {
      const itemToMerge = itemsToMerge.find(itemToMerge => {
        if (this.mode && this.mode !== 'api') {
          return currentItem.provisional_id == itemToMerge.provisional_id;
        } else {
          return currentItem.id == itemToMerge.id;
        }
      });
      return {
        ...currentItem,
        ...itemToMerge,
      };
    });
    return resultItems;
  }
  private addSelectToQueryConfig(selectConfigNameIndex: number, queryConfig: any) {
    if (this.selectConfigName && this.selectConfigName[selectConfigNameIndex]) {
      const name = this.resolvedConfigFileName.replaceAll('-', '_');
      const config = this.$store.getters['config/entityConfig'](name);
      if (
        config &&
        config.selects &&
        config.selects[this.selectConfigName[selectConfigNameIndex]]
      ) {
        const selectConfig = config.selects[this.selectConfigName[selectConfigNameIndex]];
        queryConfig.params['columns'] = selectConfig.fields ? selectConfig.fields : null;
        queryConfig.params['disambiguatedFields'] = selectConfig.disambiguatedFields
          ? selectConfig.disambiguatedFields
          : null;
        queryConfig.params['relationFields'] = selectConfig.relationFields
          ? selectConfig.relationFields
          : null;
        queryConfig.params['computedFields'] = selectConfig.computedFields
          ? selectConfig.computedFields
          : null;
        queryConfig.params['withRelations'] = selectConfig.withRelations
          ? selectConfig.withRelations
          : null;
        queryConfig.params['mediaFields'] = selectConfig.mediaFields
          ? selectConfig.mediaFields
          : null;
        // Ask the DTO to include the necessary computed fields and relation fields
        queryConfig.params['include'] = (selectConfig.computedFields ?? [])
          .map(computedField => this.camelize(computedField))
          .concat(this.parseRelationsFrom(selectConfig.relationFields ?? []));

        if (selectConfig.noCached) {
          queryConfig.headers = {
            'BPC-CACHE': 'NO-CACHED',
          };
        }
      }
    }
    return queryConfig;
  }

  private parseRelationsFrom(relationFields) {
    return relationFields
      .map(relationField => relationField.split('.'))
      .filter(parts => parts.length > 1)
      .map(parts => {
        const fieldName = parts.pop();
        return parts.join('.');
      })
      .map(relation => this.camelize(relation))
      .filter((x, index, array) => array.indexOf(x) === index); // Remove duplicates
  }

  private camelize(str) {
    return str.replace(/([-_]\w)/g, g => g[1].toUpperCase());
  }

  private addSelectedItemToQueryConfig(queryConfig: any) {
    if (this.selectedItem) {
      if (typeof this.selectedItem === 'object' && !Array.isArray(this.selectedItem)) {
        if (this.selectedItem.id) {
          // If the id is present we set it
          queryConfig.params['selectedItem'] = this.selectedItem.id;
        } else {
          // If the id is not present we have to remove the param
          if (queryConfig.params['selectedItem']) {
            delete queryConfig.params['selectedItem'];
          }
        }
      } else if (Array.isArray(this.selectedItem) && this.selectedItem.length > 0) {
        queryConfig.params['selectedItem'] = this.selectedItem.map(item => {
          let id = null;
          if (item.id) {
            // item === 'object' doesn't work
            id = item.id;
          } else {
            id = item;
          }
          return id;
        });
        queryConfig.params['selectedItem'] = queryConfig.params['selectedItem'].join(',');
      } else {
        queryConfig.params['selectedItem'] = this.selectedItem;
      }
    } else {
      if (queryConfig.params['selectedItem']) {
        delete queryConfig.params['selectedItem'];
      }
    }
  }
}
